import { g as __spread, d as __assign, f as __read, a as __extends, b as __awaiter, e as __generator, C as ClientSDKProxyContext } from '../../chunks/chunk-525a49bc.js';
import { Button, Slider } from 'antd';
import React, { Component } from 'react';
import { E as ERROR_NO_SDK_CLIENT } from '../../chunks/chunk-bc685b06.js';
import { isEqual } from 'lodash';
import { THREE, Cognite3DViewer } from '@cognite/3d-viewer';

var ViewerEventTypes;
(function (ViewerEventTypes) {
    ViewerEventTypes["progress"] = "progress";
    ViewerEventTypes["complete"] = "complete";
    ViewerEventTypes["error"] = "error";
})(ViewerEventTypes || (ViewerEventTypes = {}));
function setCameraPosition(viewer, model, revision, boundingBox) {
    var Vector3 = THREE.Vector3;
    var camera = revision.camera;
    if (!camera) {
        return;
    }
    if (boundingBox && !boundingBox.isEmpty()) {
        boundingBox.applyMatrix4(model.matrix);
        viewer.fitCameraToBoundingBox(boundingBox, 0);
    }
    else if (Array.isArray(camera.target) && Array.isArray(camera.position)) {
        // Set default camera position
        var cameraPosition = new (Vector3.bind.apply(Vector3, __spread([void 0], camera.position)))();
        var cameraTarget = new (Vector3.bind.apply(Vector3, __spread([void 0], camera.target)))();
        // Camera position and target are defined in model space, transform to world space
        cameraPosition.applyMatrix4(model.matrix);
        cameraTarget.applyMatrix4(model.matrix);
        viewer.setCameraPosition(cameraPosition);
        viewer.setCameraTarget(cameraTarget);
    }
    else {
        viewer.fitCameraToModel(model, 0);
    }
}
function createViewer(_a) {
    var _b;
    var sdk = _a.sdk, modelId = _a.modelId, revisionId = _a.revisionId, boundingBox = _a.boundingBox, cache = _a.cache, domElement = _a.domElement;
    var hash = hashGenerator(modelId, revisionId, boundingBox);
    var progress = ViewerEventTypes.progress, complete = ViewerEventTypes.complete, error = ViewerEventTypes.error;
    if (cache && cache[hash]) {
        cache[hash].fromCache = true;
        // TODO: update to TS 3.7.0 when it's out
        domElement.parentNode.replaceChild(cache[hash].viewer.domElement, domElement);
        return __assign(__assign({}, cache[hash]), { domElement: cache[hash].viewer.domElement });
    }
    var viewer = new Cognite3DViewer({ sdk: sdk, domElement: domElement, enableCache: true });
    var listeners = (_b = {},
        _b[progress] = [],
        _b[complete] = [],
        _b[error] = [],
        _b);
    var onProgress = function (progressEvent) {
        listeners[progress].forEach(function (callback) { return callback(progressEvent); });
    };
    var onComplete = function () {
        listeners[complete].forEach(function (callback) { return callback(); });
    };
    var modelPromise = viewer.addModel({
        modelId: modelId,
        revisionId: revisionId,
        geometryFilter: { boundingBox: boundingBox },
        onProgress: onProgress,
        onComplete: onComplete,
    });
    var response = {
        domElement: domElement,
        viewer: viewer,
        modelPromise: modelPromise,
        addEvent: addEvent.bind(null, listeners),
        removeEvent: removeEvent.bind(null, listeners),
    };
    if (cache) {
        cache[hash] = response;
    }
    return response;
}
function addEvent(listeners, events) {
    events.forEach(function (_a) {
        var _b = __read(_a, 2), type = _b[0], callback = _b[1];
        listeners[type].push(callback);
    });
}
function removeEvent(listeners, events) {
    if (events) {
        events.forEach(function (_a) {
            var _b = __read(_a, 2), type = _b[0], callback = _b[1];
            var index = listeners[type].indexOf(callback);
            if (index === -1) {
                return;
            }
            listeners[type].splice(index, 1);
        });
        return;
    }
    Object.values(ViewerEventTypes).forEach(function (key) {
        listeners[key] = [];
    });
}
function hashGenerator(modelId, revisionId, boundingBox) {
    var boundingBoxString;
    if (boundingBox) {
        var boundingBoxMinString = boundingBox.min.toArray().join(',');
        var boundingBoxMaxString = boundingBox.max.toArray().join(',');
        boundingBoxString = "[" + boundingBoxMinString + ", " + boundingBoxMaxString + "]";
    }
    return modelId + "/" + revisionId + (boundingBoxString ? '?boundingBox=' + boundingBoxString : '');
}

var createViewer$1 = createViewer;
var containerStyles = {
    width: '100%',
    paddingTop: '2%',
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'spaceAround',
};
var Model3DViewer = /** @class */ (function (_super) {
    __extends(Model3DViewer, _super);
    function Model3DViewer(props) {
        var _this = _super.call(this, props) || this;
        _this.state = {};
        _this.disposeCalls = [];
        _this.divWrapper = React.createRef();
        _this.inputWrapper = React.createRef();
        _this.model = null;
        _this.revision = null;
        _this.viewer = null;
        _this.nodes = [];
        _this.planes = [
            new THREE.Plane(new THREE.Vector3(1, 0, 0), Infinity),
            new THREE.Plane(new THREE.Vector3(0, 1, 0), Infinity),
            new THREE.Plane(new THREE.Vector3(0, 0, 1), Infinity),
        ];
        _this.flipped = [true, true, true];
        _this.addDisposeCall = function (func) {
            _this.disposeCalls.push(func);
        };
        _this.dispose = function () {
            _this.disposeCalls.forEach(function (func) {
                func();
            });
        };
        _this.resetCameraPosition = function () {
            var _a = _this, viewer = _a.viewer, model = _a.model, revision = _a.revision, boundingBox = _a.state.boundingBox;
            if (!viewer || !model || !revision) {
                return;
            }
            setCameraPosition(viewer, model, revision, boundingBox);
        };
        _this.onContainerClick = function () {
            if (_this.inputWrapper.current) {
                _this.inputWrapper.current.focus();
            }
        };
        _this.onBlur = function () {
            if (_this.viewer) {
                _this.viewer.disableKeyboardNavigation();
            }
        };
        _this.onFocus = function () {
            if (_this.viewer && _this.props.enableKeyboardNavigation) {
                _this.viewer.enableKeyboardNavigation();
            }
        };
        /**
         * @return a slicing plane for a specified axis
         */
        _this.sliceByAxis = function (sliceDetail, axisNumber) {
            var vector = [0, 0, 0];
            vector[axisNumber] = sliceDetail.direction ? 1 : -1;
            return new THREE.Plane(new THREE.Vector3(vector[0], vector[1], vector[2]), sliceDetail.coord);
        };
        /**
         * Slices the model based on the slicing properties passed in.
         */
        _this.slice = function (sliceProps) {
            if (_this.viewer) {
                var planes = _this.planes;
                planes[0] = sliceProps.x ? _this.sliceByAxis(sliceProps.x, 0) : planes[0];
                planes[1] = sliceProps.y ? _this.sliceByAxis(sliceProps.y, 1) : planes[1];
                planes[2] = sliceProps.z ? _this.sliceByAxis(sliceProps.z, 2) : planes[2];
                _this.planes = planes;
                _this.viewer.setSlicingPlanes(planes);
            }
        };
        _this.takeScreenshot = function () { return __awaiter(_this, void 0, void 0, function () {
            var url;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.viewer) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.viewer.getScreenshot()];
                    case 1:
                        url = _a.sent();
                        if (this.props.onScreenshot) {
                            this.props.onScreenshot(url);
                        }
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        }); };
        /**
         * Update the slicing planes as user drag the sliders.
         */
        _this.onChange = function (val, axisNumber) {
            _this.planes[axisNumber].set(_this.planes[axisNumber].normal, _this.flipped[axisNumber] ? val : -val);
            if (_this.viewer) {
                _this.viewer.setSlicingPlanes(_this.planes);
            }
        };
        /**
         * Reverse the direction of the slicing axis.
         */
        _this.flipSlider = function (axisNumber) {
            _this.planes[axisNumber].negate();
            _this.flipped[axisNumber] = !_this.flipped[axisNumber];
            if (_this.viewer) {
                _this.viewer.setSlicingPlanes(_this.planes);
            }
        };
        /**
         * Render a single slider.
         */
        _this.renderSlider = function (range, axisNumber) {
            if (!range) {
                return null;
            }
            var number2Axis = ['x', 'y', 'z'];
            return (React.createElement("div", { style: containerStyles },
                React.createElement("span", { style: { marginTop: '1%' } },
                    React.createElement("h4", null, number2Axis[axisNumber])),
                React.createElement(Slider, { step: (range.max - range.min) / 100, min: range.min, max: range.max, defaultValue: range.max, style: { width: '80%' }, onChange: function (val) {
                        return _this.onChange(val, axisNumber);
                    } }),
                React.createElement(Button, { type: "primary", icon: "redo", size: "small", style: { marginLeft: '2%' }, onClick: function () { return _this.flipSlider(axisNumber); } })));
        };
        /**
         * Render all sliders.
         */
        _this.renderSliders = function () {
            if (!_this.props.slider) {
                return null;
            }
            var xSlider = _this.renderSlider(_this.props.slider.x, 0);
            var ySlider = _this.renderSlider(_this.props.slider.y, 1);
            var zSlider = _this.renderSlider(_this.props.slider.z, 2);
            return (React.createElement("div", { style: {
                    background: 'white',
                    position: 'absolute',
                    width: '20%',
                    marginTop: '2%',
                    marginLeft: '78%',
                    padding: '1%',
                    display: 'flex',
                    flexWrap: 'wrap',
                } },
                xSlider,
                ySlider,
                zSlider));
        };
        _this.onClickHandlerBounded = _this.onClickHandler.bind(_this);
        _this.onCompleteBounded = _this.onComplete.bind(_this);
        return _this;
    }
    Model3DViewer.getDerivedStateFromProps = function (props, state) {
        var stateBoundingBox = state.boundingBox;
        var propsBoundingBox = props.boundingBox;
        if (!isEqual(stateBoundingBox, propsBoundingBox)) {
            return {
                boundingBox: propsBoundingBox ? propsBoundingBox.clone() : undefined,
            };
        }
        return null;
    };
    Model3DViewer.prototype.componentDidMount = function () {
        return __awaiter(this, void 0, void 0, function () {
            var sdk, _a, modelId, revisionId, cache, enableKeyboardNavigation, useDefaultCameraPosition, onProgress, onReady, onCameraChange, onError, slice, boundingBox, progress, complete, _b, viewer, addEvent, removeEvent, modelPromise, fromCache, domElement, model, revision, nodes, e_1;
            var _c;
            var _this = this;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        this.client = this.context('Model3DViewer');
                        if (!this.client) {
                            console.error(ERROR_NO_SDK_CLIENT);
                            return [2 /*return*/];
                        }
                        if (!this.divWrapper.current || !this.inputWrapper.current) {
                            return [2 /*return*/];
                        }
                        sdk = this.client;
                        _a = this.props, modelId = _a.modelId, revisionId = _a.revisionId, cache = _a.cache, enableKeyboardNavigation = _a.enableKeyboardNavigation, useDefaultCameraPosition = _a.useDefaultCameraPosition, onProgress = _a.onProgress, onReady = _a.onReady, onCameraChange = _a.onCameraChange, onError = _a.onError, slice = _a.slice;
                        boundingBox = this.state.boundingBox;
                        progress = ViewerEventTypes.progress, complete = ViewerEventTypes.complete;
                        _b = createViewer$1({
                            sdk: sdk,
                            modelId: modelId,
                            revisionId: revisionId,
                            boundingBox: boundingBox,
                            cache: cache,
                            domElement: this.divWrapper.current,
                        }), viewer = _b.viewer, addEvent = _b.addEvent, removeEvent = _b.removeEvent, modelPromise = _b.modelPromise, fromCache = _b.fromCache, domElement = _b.domElement;
                        this.viewer = viewer;
                        // Looks like replaceChild looses onClick event handler, so adding it this way instead
                        domElement.addEventListener('click', this.onContainerClick);
                        if (!enableKeyboardNavigation) {
                            this.viewer.disableKeyboardNavigation();
                        }
                        if (onProgress) {
                            addEvent([[progress, onProgress]]);
                        }
                        addEvent([[complete, this.onCompleteBounded]]);
                        this.addDisposeCall(function () {
                            domElement.removeEventListener('click', _this.onContainerClick);
                            removeEvent();
                        });
                        _d.label = 1;
                    case 1:
                        _d.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, Promise.all([
                                modelPromise,
                                this.getRevision(),
                                this.findMappedNodes(),
                            ])];
                    case 2:
                        _c = __read.apply(void 0, [_d.sent(), 3]), model = _c[0], revision = _c[1], nodes = _c[2];
                        return [3 /*break*/, 4];
                    case 3:
                        e_1 = _d.sent();
                        if (onError) {
                            onError(e_1);
                        }
                        this.dispose();
                        return [2 /*return*/];
                    case 4:
                        this.model = model;
                        this.revision = revision;
                        this.nodes = nodes;
                        if (useDefaultCameraPosition && !fromCache) {
                            this.resetCameraPosition();
                        }
                        if (onReady) {
                            onReady(viewer, model, revision);
                        }
                        viewer.on('click', this.onClickHandlerBounded);
                        if (onCameraChange) {
                            viewer.on('cameraChange', onCameraChange);
                        }
                        if (slice) {
                            this.slice(slice);
                        }
                        this.addDisposeCall(function () {
                            viewer.off('click', _this.onClickHandlerBounded);
                            if (onCameraChange) {
                                viewer.off('cameraChange', onCameraChange);
                            }
                        });
                        return [2 /*return*/];
                }
            });
        });
    };
    Model3DViewer.prototype.componentDidUpdate = function (prevProps) {
        return __awaiter(this, void 0, void 0, function () {
            var _a, onError, assetId, pAssetId, _b, error_1;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _a = this.props, onError = _a.onError, assetId = _a.assetId;
                        pAssetId = prevProps.assetId;
                        if (assetId === pAssetId) {
                            return [2 /*return*/];
                        }
                        _c.label = 1;
                    case 1:
                        _c.trys.push([1, 3, , 4]);
                        _b = this;
                        return [4 /*yield*/, this.findMappedNodes()];
                    case 2:
                        _b.nodes = _c.sent();
                        this.highlightMappedNodesIfAllowed();
                        return [3 /*break*/, 4];
                    case 3:
                        error_1 = _c.sent();
                        if (onError) {
                            onError(error_1);
                        }
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    Model3DViewer.prototype.componentWillUnmount = function () {
        this.dispose();
    };
    Model3DViewer.prototype.onClickHandler = function (_a) {
        var offsetX = _a.offsetX, offsetY = _a.offsetY;
        var onClick = this.props.onClick;
        if (!this.viewer || !onClick) {
            return;
        }
        var intersection = this.viewer.getIntersectionFromPixel(offsetX, offsetY);
        if (intersection === null) {
            onClick(null);
        }
        else {
            onClick(intersection.nodeId, intersection.point);
        }
    };
    Model3DViewer.prototype.render = function () {
        var styles = this.props.styles;
        return (
        // Need this div since caching uses replaceChild on divWrapper ref, so need a surrounding div
        React.createElement("div", { style: __assign({ width: '100%', height: '100%', position: 'relative' }, (styles && styles.wrapper)) },
            this.props.showScreenshotButton ? (React.createElement("div", { style: {
                    position: 'absolute',
                    marginTop: '2%',
                    marginLeft: '2%',
                } },
                React.createElement(Button, { onClick: this.takeScreenshot }, "Take ScreenShot"))) : null,
            this.renderSliders(),
            React.createElement("input", { readOnly: true, type: "text", onBlur: this.onBlur, onFocus: this.onFocus, ref: this.inputWrapper, style: {
                    opacity: 0,
                    pointerEvents: 'none',
                    position: 'absolute',
                } }),
            React.createElement("div", { style: __assign({ width: '100%', height: '100%', fontSize: 0 }, (styles && styles.viewer)), ref: this.divWrapper }, this.props.children)));
    };
    Model3DViewer.prototype.findMappedNodes = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a, assetId, modelId, revisionId;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _a = this.props, assetId = _a.assetId, modelId = _a.modelId, revisionId = _a.revisionId;
                        if (!assetId) {
                            return [2 /*return*/, Promise.resolve([])];
                        }
                        return [4 /*yield*/, this.client.assetMappings3D
                                .list(modelId, revisionId, {
                                assetId: assetId,
                            })
                                .autoPagingToArray()];
                    case 1: return [2 /*return*/, _b.sent()];
                }
            });
        });
    };
    Model3DViewer.prototype.getRevision = function () {
        var _a = this.props, modelId = _a.modelId, revisionId = _a.revisionId;
        return this.client.revisions3D.retrieve(modelId, revisionId);
    };
    Model3DViewer.prototype.highlightMappedNodesIfAllowed = function () {
        var _this = this;
        var highlightMappedNodes = this.props.highlightMappedNodes;
        if (!highlightMappedNodes) {
            return;
        }
        var length = this.nodes.length;
        if (!this.model || !this.viewer) {
            return;
        }
        this.model.deselectAllNodes();
        if (length === 1) {
            var nodeId = this.nodes[0].nodeId;
            this.model.updateMatrixWorld();
            var reusableBox = new THREE.Box3();
            var bb = this.model.getBoundingBox(nodeId, reusableBox);
            this.model.selectNode(nodeId);
            if (!bb.isEmpty()) {
                this.viewer.fitCameraToBoundingBox(bb);
            }
        }
        else {
            this.nodes.forEach(function (node) {
                // @ts-ignore
                return _this.model.selectNode(node.nodeId);
            });
        }
    };
    Model3DViewer.prototype.onComplete = function () {
        var _a = this.props, onComplete = _a.onComplete, assetId = _a.assetId;
        if (assetId) {
            this.highlightMappedNodesIfAllowed();
        }
        if (onComplete) {
            onComplete();
        }
    };
    Model3DViewer.defaultProps = {
        enableKeyboardNavigation: true,
        highlightMappedNodes: true,
        useDefaultCameraPosition: true,
        showScreenshotButton: false,
    };
    Model3DViewer.contextType = ClientSDKProxyContext;
    return Model3DViewer;
}(Component));

export { Model3DViewer };
